import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CLUSTERS } from '../data/clusterConfig';
import {
  Shield, Moon, Brain, Eye, Zap, GraduationCap, Heart, ChevronRight, ArrowRight
} from 'lucide-react';

const CLUSTER_META = {
  'anksiyete-testleri': { icon: Shield, color: '#D88A61', desc: 'Genel kaygı, sosyal anksiyete, gece kaygısı ve performans kaygısı değerlendirmeleri.' },
  'uyku-testleri': { icon: Moon, color: '#D9A258', desc: 'Uyku kalitesi, uykusuzluk, sabah yorgunluğu ve uyku düzeni analizleri.' },
  'hafiza-ve-beyin-testleri': { icon: Brain, color: '#5A9E7A', desc: 'Kısa süreli hafıza, bilişsel esneklik ve beyin yaşı değerlendirmeleri.' },
  'dikkat-ve-odak-testleri': { icon: Eye, color: '#6F90B1', desc: 'Dikkat süresi, odaklanma kapasitesi ve dijital dikkat ölçümleri.' },
  'stres-ve-tukenmislik-testleri': { icon: Zap, color: '#C25D4F', desc: 'İş stresi, duygusal tükenmişlik ve stres yönetimi değerlendirmeleri.' },
  'ogrenci-performans-testleri': { icon: GraduationCap, color: '#7B8EA8', desc: 'Sınav stresi, ders verimi ve motivasyon analizleri.' },
  'yasam-tarzi-testleri': { icon: Heart, color: '#8E9BAA', desc: 'Dijital alışkanlıklar, yaşam kalitesi ve sosyal ilişki değerlendirmeleri.' },
};

const CLUSTER_ORDER = [
  'anksiyete-testleri',
  'uyku-testleri',
  'hafiza-ve-beyin-testleri',
  'dikkat-ve-odak-testleri',
  'stres-ve-tukenmislik-testleri',
  'ogrenci-performans-testleri',
  'yasam-tarzi-testleri',
];

export default function TestlerPage() {
  const totalTests = CLUSTER_ORDER.reduce((sum, slug) => {
    const c = CLUSTERS[slug];
    return sum + (c ? c.tests.length : 0);
  }, 0);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #15263A 0%, #1E3348 80px, #F8F6F3 80px, #F8F6F3 100%)' }}>
      <Helmet>
        <title>Tüm Testler — DoktorCebinde</title>
        <meta name="description" content="Stres, uyku, hafıza, dikkat, kaygı ve yaşam tarzı alanlarında 37'den fazla ücretsiz tarama testi." />
      </Helmet>
      <Header />

      {/* Hero */}
      <section style={{ paddingTop: 48, paddingBottom: 40 }}>
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#94A3B8', marginBottom: 12 }}>{totalTests}+ Ücretsiz Test</p>
          <h1 style={{ fontSize: 32, fontWeight: 700, color: '#1A2B3C', lineHeight: 1.2, marginBottom: 12 }} data-testid="testler-page-title">
            Tüm Testler
          </h1>
          <p style={{ color: '#687482', fontSize: 15, maxWidth: 500, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>
            Stres, uyku, hafıza, dikkat, kaygı ve yaşam tarzı alanlarında kendinizi değerlendirin. Tüm testler ücretsizdir ve sonuçlarınız anında gösterilir.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-5xl mx-auto px-4 pb-20" data-testid="testler-categories">
        <div className="space-y-8">
          {CLUSTER_ORDER.map(slug => {
            const cluster = CLUSTERS[slug];
            if (!cluster) return null;
            const meta = CLUSTER_META[slug] || {};
            const Icon = meta.icon || Brain;
            return (
              <div key={slug} style={{ background: '#fff', border: '1px solid #ECE7E1', borderRadius: 16, overflow: 'hidden' }} data-testid={`testler-category-${slug}`}>
                {/* Category Header with accent */}
                <div style={{ borderBottom: '1px solid #ECE7E1' }}>
                  <div style={{ height: 3, background: meta.color }} />
                  <div className="flex items-center gap-3" style={{ padding: '20px 24px' }}>
                    <div className="flex items-center justify-center" style={{ width: 40, height: 40, borderRadius: 12, background: `${meta.color}10` }}>
                      <Icon style={{ width: 20, height: 20, color: meta.color }} />
                    </div>
                    <div className="flex-1">
                      <Link to={`/${slug}`} className="group flex items-center gap-2">
                        <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1A2B3C', margin: 0 }}>{cluster.heading}</h2>
                        <ChevronRight style={{ width: 16, height: 16, color: meta.color }} className="transition-transform group-hover:translate-x-1" />
                      </Link>
                      <p style={{ fontSize: 13, color: '#687482', margin: 0, marginTop: 2 }}>{meta.desc}</p>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: meta.color, background: `${meta.color}10`, padding: '4px 10px', borderRadius: 8 }}>{cluster.tests.length} test</span>
                  </div>
                </div>

                {/* Tests Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" style={{ gap: 0 }}>
                  {cluster.tests.map((test, idx) => (
                    <Link
                      key={test.slug}
                      to={`/${test.slug}`}
                      className="group flex items-center gap-3 transition-colors"
                      style={{
                        padding: '14px 24px',
                        borderBottom: idx < cluster.tests.length - 1 ? '1px solid #f5f1ed' : 'none',
                        borderRight: '1px solid #f5f1ed',
                      }}
                      data-testid={`testler-link-${test.slug}`}
                    >
                      <div style={{ width: 5, height: 5, borderRadius: '50%', flexShrink: 0, background: meta.color, opacity: 0.5 }} />
                      <div className="flex-1 min-w-0">
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#1A2B3C' }} className="group-hover:text-blue-700 transition-colors">
                          {test.name}
                        </span>
                        <p className="line-clamp-1" style={{ fontSize: 12, color: '#94A3B8', marginTop: 1, marginBottom: 0 }}>{test.desc}</p>
                      </div>
                      <ArrowRight style={{ width: 14, height: 14, color: '#CBD5E1', flexShrink: 0 }} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
}
